
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';

import { formatCurrency, formatNumberWithSpaces } from '../../../../../../common-utils/formatters';
import type { MarketVisionSettings } from '../../../../../@types/Company';
import {
  setNbMandates,
  setNbPreSaleStatements,
  setNbSaleStatements,
  setPricePreSaleStatements,
  setRealSales,
} from '../../../../../store/SalesDataReducer';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import tailwindConfig from '../../../../../utils/tailwindColors';
import { showToast } from '../../../../common/Notification';
import Table from '../../../../common/Table';

interface FinancialData {
  id: string;
  'SIMULATION': string | JSX.Element;
  [year: string]: string | JSX.Element;
}

type TimeoutHandle = ReturnType<typeof setTimeout>;

function PerformanceAnalysis() {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const {
    years,
    salesData,
    nbSaleStatements,
    nbMandates,
    nbPreSaleStatements,
    pricePreSaleStatements,
    realSales,
  } = useSelector((state: RootState) => state.salesData);

  const [formattedFinancialData, setFormattedFinancialData] = useState<FinancialData[]>([]);
  const updateTimeoutRef = useRef<TimeoutHandle | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (company) {
      dispatch(setNbSaleStatements(company.nbSaleStatements || {}));
      dispatch(setNbMandates(company.nbMandates || {}));
      dispatch(setNbPreSaleStatements(company.nbPreSaleStatements || {}));
      dispatch(setPricePreSaleStatements(company.pricePreSaleStatements || { value: '' }));
      dispatch(setRealSales(company.realSales || {}));
    }
  }, [company, dispatch]);

  const updateMarketVisionSettings = useCallback((marketVisionSettingsLabel: string, updatedValue: MarketVisionSettings | string) => {
    if (!company || !jwt || !company.id) {return;}

    const updatedMarketVisionSettings = {
      realSales: marketVisionSettingsLabel === 'realSales' ? updatedValue : { ...realSales },
      nbMandates: marketVisionSettingsLabel === 'nbMandates' ? updatedValue : { ...nbMandates },
      nbPreSaleStatements: marketVisionSettingsLabel === 'nbPreSaleStatements' ? updatedValue : { ...nbPreSaleStatements },
      pricePreSaleStatements: marketVisionSettingsLabel === 'pricePreSaleStatements' ? updatedValue : { ...pricePreSaleStatements },
      nbSaleStatements: marketVisionSettingsLabel === 'nbSaleStatements' ? updatedValue : { ...nbSaleStatements },
    };

    fetch(`/api/companies/${company.id}/market-settings`, {
      method: 'PATCH',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        commission: company.commission,
        ...updatedMarketVisionSettings,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to update market vision settings');
        }
        return response.json();
      })
      .catch((fetchError) => {
        console.error('Error updating real sales :', fetchError);
        showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
      });
  }, [company, jwt, nbSaleStatements, nbMandates, nbPreSaleStatements, pricePreSaleStatements, realSales]);

  const handleMarketVisionSettingsChange = (marketVisionSettingsLabel: string, year: string, value: string) => {
    if (!company || !jwt || !company.id) {return;}

    const formattedValue = formatNumberWithSpaces(value.replaceAll(/\D/g, ''));
    // Remove " (en cours d'enregistrement)" from the year if present
    const actualYear = year.split(' ')[0];

    let updatedState;
    switch (marketVisionSettingsLabel) {
    case 'nbSaleStatements':
      updatedState = { ...nbSaleStatements, [actualYear]: formattedValue };
      dispatch(setNbSaleStatements(updatedState));
      break;
    case 'nbMandates':
      updatedState = { ...nbMandates, [actualYear]: formattedValue };
      dispatch(setNbMandates(updatedState));
      break;
    case 'nbPreSaleStatements':
      updatedState = { ...nbPreSaleStatements, [actualYear]: formattedValue };
      dispatch(setNbPreSaleStatements(updatedState));
      break;
    case 'pricePreSaleStatements':
      updatedState = { ...pricePreSaleStatements, value: formattedValue };
      dispatch(setPricePreSaleStatements(updatedState));
      break;
    case 'realSales':
      updatedState = { ...realSales, [actualYear]: formattedValue };
      dispatch(setRealSales(updatedState));
      break;
    }

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      updateMarketVisionSettings(marketVisionSettingsLabel, updatedState);
    }, 1000);
  };

  const generateYearDataFromState = (
    marketVisionSettings: MarketVisionSettings,
    formatter: (value: string | number | undefined, year: string) => string | JSX.Element = (value) => String(value ?? ''),
  ) => {
    return years.reduce((acc, year) => {
      const actualYear = year.split(' ')[0];
      acc[year] = marketVisionSettings.value 
        ? formatter(marketVisionSettings.value, actualYear) 
        : formatter(marketVisionSettings[actualYear], actualYear);
      return acc;
    }, {} as Record<string, string | JSX.Element>);
  };

  const formatSimulationData = (): FinancialData[] => {
    if (!salesData || Object.keys(salesData).length === 0) {return [];}

    return [
      {
        id: '1',
        'SIMULATION': 'Nb d\'états datés réalisés',
        ...generateYearDataFromState(nbSaleStatements, (_, year) => (
          <input
            type="text"
            value={(nbSaleStatements && nbSaleStatements[year]) || ''}
            onChange={(e) => handleMarketVisionSettingsChange('nbSaleStatements', year, e.target.value)}
            className="max-w-28 rounded border border-gray-300 p-0"
          />
        )),
      },
      {
        id: '2',
        'SIMULATION': 'Nb de mandats rentrés sur vos copros',
        ...generateYearDataFromState(nbMandates, (_, year) => (
          <input
            type="text"
            value={(nbMandates && nbMandates[year]) || ''}
            onChange={(e) => handleMarketVisionSettingsChange('nbMandates', year, e.target.value)}
            className="max-w-28 rounded border border-gray-300 p-0"
          />
        )),
      },
      {
        id: '3',
        'SIMULATION': 'Nb de pré-états datés réalisés',
        ...generateYearDataFromState(nbPreSaleStatements, (_, year) => (
          <input
            type="text"
            value={(nbPreSaleStatements && nbPreSaleStatements[year]) || ''}
            onChange={(e) => handleMarketVisionSettingsChange('nbPreSaleStatements', year, e.target.value)}
            className="max-w-28 rounded border border-gray-300 p-0"
          />
        )),
      },
      {
        id: '4',
        'SIMULATION': <span id="pricePreSaleStatements">Prix moyen pré-état daté</span>,
      },
      {
        id: '5',
        'SIMULATION': 'CA estimé des pré-états datés',
        ...generateYearDataFromState(nbPreSaleStatements, (_, year) => {
          const nbPreSaleStatementsVal = typeof nbPreSaleStatements[year] === 'string' ? parseInt(nbPreSaleStatements[year].replaceAll(/\D/g, ''), 10) : nbPreSaleStatements[year];
          const pricePreSaleStatementsVal = typeof pricePreSaleStatements.value === 'string' ? parseInt(pricePreSaleStatements.value.replaceAll(/\D/g, ''), 10) : pricePreSaleStatements.value;
          if (isNaN(nbPreSaleStatementsVal * pricePreSaleStatementsVal) || nbPreSaleStatementsVal * pricePreSaleStatementsVal == 0) {
            return <i>Compléter Prix et Nb de pré-états</i>;
          }
          return (nbPreSaleStatementsVal && pricePreSaleStatementsVal ? (
            <span>
              {formatCurrency(nbPreSaleStatementsVal * pricePreSaleStatementsVal)}
            </span>
          ) : null);
        }),
      },
      {
        id: '6',
        'SIMULATION': <span className="font-semibold">Montant des honoraires sur ces mandats</span>,
        ...generateYearDataFromState(realSales, (_, year) => (
          <span>
            <input
              type="text"
              value={(realSales && realSales[year]) || ''}
              onChange={(e) => handleMarketVisionSettingsChange('realSales', year, e.target.value)}
              className="max-w-28 rounded border border-gray-300 p-0"
            />
            {' '}€
          </span>
        )),
      },
      {
        id: '7',
        'SIMULATION': <span className="font-semibold">Progrès possible avec Déclarations Juridiques</span>,
        ...generateYearDataFromState(realSales, (_, year) => {
          const realSale = typeof realSales[year] === 'string' ? parseInt(realSales[year].replaceAll(/\D/g, ''), 10) : realSales[year];
          const potentialSale = salesData[year] ? parseInt(salesData[year].salesCommission, 10) : 0;
          const diff = potentialSale - realSale;
          if (isNaN(diff) || !realSale) {
            return <i>Compléter le Montant des honoraires</i>;
          }
          return (
            <span>
              {formatCurrency(diff)}
            </span>
          );
        }),
      },
    ];
  };

  useEffect(() => {
    if (!salesData || !nbSaleStatements || !nbMandates || !nbPreSaleStatements || !pricePreSaleStatements || !realSales || !(years && years.length > 0)) {
      setIsLoading(true);
      return;
    }
    setFormattedFinancialData(formatSimulationData());
    setIsLoading(false);
  }, [salesData, nbSaleStatements, nbMandates, nbPreSaleStatements, pricePreSaleStatements, realSales, years]);

  useEffect(() => () => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
  }, []);

  const simulationHeaders = ['SIMULATION', ...years];

  const mergedCellsConfig = (item: FinancialData) => {
    const config: { [header: string]: { colSpan: number; content: string | JSX.Element } } = {};
    if (item.SIMULATION && typeof item.SIMULATION === 'object' && item.SIMULATION !== null && 'props' in item.SIMULATION && (item.SIMULATION as React.ReactElement).props?.id === 'pricePreSaleStatements') {
      const totalYears = years.length;
      years.forEach((key, index) => {
        if (index === 0) {
          config[key] = {
            colSpan: totalYears,
            content:
            <span>
              <input
                type="text"
                value={(pricePreSaleStatements && pricePreSaleStatements.value) || ''}
                onChange={(e) => handleMarketVisionSettingsChange('pricePreSaleStatements', 'value', e.target.value)}
                className="max-w-28 rounded border border-gray-300 p-0"
              />
              {' '}€
            </span>,
          };
        } else {
          config[key] = { colSpan: 0, content: '' };
        }
      });
    }

    return config;
  };

  return (
    <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div 
        className="flex cursor-pointer items-center justify-between bg-rgpd-blue px-4 py-3.5 transition-all duration-200 hover:brightness-105"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className="grow text-center text-xs font-semibold tracking-wider text-white uppercase">Analyse de votre performance</h2>
        <span className="text-sm text-white transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </span>
      </div>
      {isOpen && (
        <div>
          {isLoading ? (
            <div className="w-full text-center">
              <Table data={[]} headers={['SIMULATION', '. . . .', '. . . .', '. . . .', '. . . .']} tableClass="w-full" />
              <Skeleton count={7} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-1" />
            </div>
          ) : (
            <Table 
              autoresize={false} 
              data={formattedFinancialData} 
              headers={simulationHeaders} 
              mergedCellsConfig={mergedCellsConfig} 
              tableClass="w-full min-h-56" 
            />
          )}
        </div>
      )}
    </div>
  );
}

export default PerformanceAnalysis;
